import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
// @ts-ignore no types for this package :(
import TimeMe from 'timeme.js';
import { validateDwellBaseEvent } from './eventValidation';
const DEFAULT_TIMING = {
  initialPollInterval: 5,
  pollBackoffRate: 2,
  idleTimeout: 30
};

/**
 * Helper class to record the amount of time a user has spent on a page.
 *
 * Two kinds of dwell events are fired. Dwell milestone events are fired periodically while the
 * user is on the page, at increasing intervals (by default, 5s, 10s, 20s, 40s, etc.). A final
 * dwell event will also be fired when the user leaves the page.
 *
 * Note that the event data passed to this helper should be compatible with a 'track' event.
 * See https://extranet.atlassian.com/display/PData/Track+Events for more information.
 *
 * Example usage:
 *
 * const baseEventData = {
 *   actionSubject: 'page',
 *   actionSubjectId: 'aa4fd6c5-28f4-40fa-807a-765a39957cb5',
 *   source: 'page',
 *   containerType: 'space',
 *   containerId: '340f1acf-d15b-4677-88c6-4671d23fb131',
 * }
 *
 * const helper = new DwellTimeHelper(sendTrackEventFunction, baseEventData, searchSessionId)
 * helper.start();
 */
export default class DwellTimeHelper {
  /**
   * Callback for sending a 'track' analytics event. This callback should either be the
   * 'sendTrackEvent' method on the AnalyticsWebClient, or a method that eventually delegates to it.
   * The data for the dwell event will be passed to this method for sending.
   *
   * @callback trackCallback
   * @param {Object} eventData - the base event data that will be added to the track event.
   */

  /**
   * Creates and initialises the DwellTimeHelper.
   *
   * @param {trackCallback} trackCallback - the track callback responsible for actually sending
   * the event.
   * @param {Object} eventData - the base data that will be added to the track event.
   * Additional dwell time attributes will be appended to this data. Note that you don't need to
   * include the 'action' property, as this will be added automatically.
   * @param {?string} searchSessionId - a unique identifier for the related search session.
   * @param {?Object} timingOptions - for the purpose of debugging, you can override the default
   * timing used for measuring dwell time. The following values are possible:
   * - initialPollInterval: how long to wait before the first dwell milestone event
   * is fired (in seconds). Default = 5.
   * - pollBackoffRate: how much to multiply the last poll interval by on each dwell milestone.
   * event. Default = 2.
   * - idleTimeout: how long to wait for no user activity before pausing
   * the dwell timer (in seconds). Default = 30s.
   * @param {?DwellTimeHelperOptions} dwellTimeHelperOptions - options provided to customise behaviour
   * of the helper.
   * - iframeDwell = enable detection of activity in iframes.
   */
  constructor(trackCallback, _eventData, _searchSessionId, timingOptions, dwellTimeHelperOptions) {
    _defineProperty(this, "timeMeInitialized", false);
    /**
     * Starts the dwell timer. This method should be called upon the initial page load.
     */
    _defineProperty(this, "start", () => {
      if (this.timeMeInitialized) {
        throw new Error('DwellTimeHelper should only be initialised once per page load.');
      }
      this.timeMeInitialized = true;
      this._initDwellTimeCounterAndPolling();
      if (typeof window !== 'undefined') {
        window.addEventListener('beforeunload', () => {
          this.stop();
        });
      }
    });
    /**
     * Stops the dwell timer and sends the final dwell event. Call this method if you wish to
     * finalise a dwell measurement without starting a new one. When switching to a new page in a
     * single-page app, use newPage instead.
     */
    _defineProperty(this, "stop", () => {
      if (TimeMe.isUserCurrentlyOnPage) {
        this._sendDwellEvent(this._eventData, this._searchSessionId, true);
      }
      TimeMe.stopAllTimers();
      TimeMe.resetAllRecordedPageTimes();
      TimeMe.timeElapsedCallbacks = [];
      this.timeMeInitialized = false;
    });
    /**
     * Send the final dwell event and reinitialise the dwell timer for a new page.
     * This method should be called whenever a new page is switched to in a single-page app context.
     *
     * @param {Object} eventData - the base data that will be added to the track event.
     * @param {?string} searchSessionId - the new search session ID.
     */
    _defineProperty(this, "newPage", (eventData, searchSessionId) => {
      validateDwellBaseEvent(eventData);
      this.stop();
      this._eventData = eventData;
      this._searchSessionId = searchSessionId;
      this._initDwellTimeCounterAndPolling();
    });
    /**
     * This is a modified version of timeit's original listForIdleEvents function,
     * which also listens to events inside same-origin iframes (such as Confluence whiteboards and databases).
     * https://github.com/jasonzissman/TimeMe.js/blob/master/timeme.js#L338
     */
    _defineProperty(this, "_listForIdleEvents", () => {
      document.addEventListener('mousemove', () => {
        TimeMe.userActivityDetected();
      });
      document.addEventListener('keyup', () => {
        TimeMe.userActivityDetected();
      });
      document.addEventListener('touchstart', () => {
        TimeMe.userActivityDetected();
      });
      window.addEventListener('scroll', () => {
        TimeMe.userActivityDetected();
      });

      /**
       * Find all same origin iframes (same origin allow access to contentWindow.document)
       * and add event listeners to them too.
       */
      const iframes = document.getElementsByTagName('iframe');
      for (let i = 0; i < iframes.length; i++) {
        const iframe = iframes[i];
        try {
          var _iframe$contentWindow, _iframe$contentWindow2, _iframe$contentWindow3;
          iframe === null || iframe === void 0 || (_iframe$contentWindow = iframe.contentWindow) === null || _iframe$contentWindow === void 0 || _iframe$contentWindow.document.addEventListener('mousemove', () => {
            TimeMe.userActivityDetected();
          });
          iframe === null || iframe === void 0 || (_iframe$contentWindow2 = iframe.contentWindow) === null || _iframe$contentWindow2 === void 0 || _iframe$contentWindow2.document.addEventListener('keyup', () => {
            TimeMe.userActivityDetected();
          });
          iframe === null || iframe === void 0 || (_iframe$contentWindow3 = iframe.contentWindow) === null || _iframe$contentWindow3 === void 0 || _iframe$contentWindow3.document.addEventListener('touchstart', () => {
            TimeMe.userActivityDetected();
          });
        } catch (e) {
          // do nothing: We don't want to listen to cross-origin iframes
        }
      }
      setInterval(() => {
        if (TimeMe.isUserCurrentlyIdle !== true) {
          TimeMe.checkIdleState();
          // This ensures the timer continues when switching between the iframe and the rest of the page
          TimeMe.startTimer();
        }
      }, TimeMe.checkIdleStateRateMs);
    });
    _defineProperty(this, "_initDwellTimeCounterAndPolling", () => {
      var _this$_dwellTimeHelpe;
      // If iframeDwell is enabled, override the listener for idle events to also listen to events in iframes.
      if ((_this$_dwellTimeHelpe = this._dwellTimeHelperOptions) !== null && _this$_dwellTimeHelpe !== void 0 && _this$_dwellTimeHelpe.iframeDwell) {
        TimeMe.listForIdleEvents = this._listForIdleEvents;
      }
      TimeMe.initialize({
        idleTimeoutInSeconds: this._timing.idleTimeout
      });
      this._pollDwellTime(this._timing.initialPollInterval, this._searchSessionId, this._eventData);
    });
    _defineProperty(this, "_pollDwellTime", (pollTime, searchSessionId, eventData) => {
      TimeMe.callAfterTimeElapsedInSeconds(pollTime, () => {
        this._sendDwellEvent(eventData, searchSessionId, false);
        this._pollDwellTime(pollTime * this._timing.pollBackoffRate, searchSessionId, eventData);
      });
    });
    _defineProperty(this, "_sendDwellEvent", (eventData, searchSessionId, isFinalDwellEvent) => {
      const dwellAttributes = {
        dwellTime: TimeMe.getTimeOnCurrentPageInMilliseconds(),
        searchReferrer: searchSessionId,
        finalDwellEvent: isFinalDwellEvent
      };
      this._trackCallback({
        action: 'dwelled',
        actionSubject: eventData.actionSubject,
        actionSubjectId: eventData.actionSubjectId,
        source: eventData.source,
        containerType: eventData.containerType,
        containerId: eventData.containerId,
        containers: eventData.containers,
        objectType: eventData.objectType,
        objectId: eventData.objectId,
        attributes: _objectSpread(_objectSpread({}, dwellAttributes), eventData.attributes),
        tags: eventData.tags
      });
    });
    if (!trackCallback) {
      throw new Error('trackCallback is required for DwellTimeHelper');
    }
    validateDwellBaseEvent(_eventData);
    this._trackCallback = trackCallback;
    this._eventData = _eventData;
    this._searchSessionId = _searchSessionId;
    this._timing = _objectSpread(_objectSpread({}, DEFAULT_TIMING), timingOptions);
    this._dwellTimeHelperOptions = dwellTimeHelperOptions;
  }
}